import { createApi } from '@reduxjs/toolkit/query/react';

import { pharmacyTags } from '@/api/pharmacy/tags';
import {
  TaskActiveFilter,
  TaskDrugShortage,
  TaskIndividualPayload,
  TransformVaccinationTask,
  VaccinationArchiveRecord,
  VaccinationTaskResponse,
} from '@/pages/Vaccination/components/task/types';
import { PageableRequest, PageableResponse } from '@/shared/api/types';
import {
  TASK,
  TASK_ACTIVE,
  TASK_ARCHIVE,
  TASK_INDIVIDUAL,
  TASK_INDIVIDUAL_BY_ID,
  TASK_PENDING,
  TASK_PROTOCOL_APPROVE,
} from '@/shared/vaccination/api';
import { groupProtocolsByDivision } from '@/utils/task';
import { mapSortToQueryParameter } from '@/utils/utils';

import { baseQueryWithReAuth } from '../../baseApi';
import { vaccinationTaskTags } from './tags';

export const vaccinationTaskApi = createApi({
  reducerPath: 'vaccinationTaskApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(vaccinationTaskTags),
  endpoints: (builder) => ({
    createTask: builder.mutation<void, void>({
      query: () => ({
        url: TASK,
        method: 'POST',
      }),
      invalidatesTags: [vaccinationTaskTags.TASK, pharmacyTags.HAS_EXPENSE],
    }),
    persistPendingTask: builder.mutation<void, string>({
      query: (payload) => ({
        url: `${TASK}/pending`,
        method: 'POST',
        params: {
          taskId: payload,
        },
      }),
      invalidatesTags: [vaccinationTaskTags.TASK, pharmacyTags.HAS_EXPENSE],
    }),
    deletePendingTask: builder.mutation<void, string>({
      query: (payload) => ({
        url: TASK_PENDING,
        method: 'DELETE',
        params: {
          taskId: payload,
        },
      }),
    }),
    getTaskPendingId: builder.query<string, TaskActiveFilter>({
      query: (payload) => ({
        url: TASK_PENDING,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
    }),
    getDrugsShortage: builder.query<TaskDrugShortage[], string>({
      query: (payload) => ({
        url: `${TASK}/drugShortage`,
        method: 'GET',
        params: {
          taskId: payload,
        },
      }),
    }),
    getActiveTask: builder.query<TransformVaccinationTask | null, TaskActiveFilter>({
      query: (payload) => ({
        url: TASK_ACTIVE,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
      transformResponse: (response: VaccinationTaskResponse) => {
        if (!response) {
          return response;
        }

        return {
          ...response,
          protocols: groupProtocolsByDivision(response?.eventProtocols),
        };
      },
      providesTags: [vaccinationTaskTags.TASK],
    }),
    getTasksArchive: builder.query<
      PageableResponse<VaccinationArchiveRecord>,
      PageableRequest
    >({
      queryFn: async (payload, _api, _opt, baseQuery) => {
        const result = await baseQuery({
          url: TASK_ARCHIVE,
          method: 'GET',
          params: {
            ...payload.filters,
            page: payload?.paging?.page,
            size: payload?.paging?.size,
            sort: mapSortToQueryParameter(payload?.sort),
          },
        });

        return { data: result?.data as PageableResponse<VaccinationArchiveRecord> };
      },
      providesTags: [vaccinationTaskTags.TASKS_ARCHIVE],
    }),
    updateTaskIndividuals: builder.mutation<void, TaskIndividualPayload[]>({
      query: (payload) => ({
        url: TASK_INDIVIDUAL,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [vaccinationTaskTags.TASK],
    }),
    updateTaskIndividual: builder.mutation<void, TaskIndividualPayload>({
      query: (payload) => ({
        url: TASK_INDIVIDUAL_BY_ID(payload.id),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [vaccinationTaskTags.TASK],
    }),
    approveEventProtocol: builder.mutation<void, { id: string }>({
      query: (payload) => ({
        url: TASK_PROTOCOL_APPROVE(payload.id),
        method: 'POST',
      }),
      invalidatesTags: [vaccinationTaskTags.TASK],
    }),
    hasWithUnexpensedDrugs: builder.query<boolean, void>({
      query: () => ({
        url: `${TASK}/hasWithUnexpensedDrugs`,
        method: 'GET',
      }),
      providesTags: [pharmacyTags.HAS_EXPENSE],
    }),
  }),
});

export const {
  useCreateTaskMutation,
  usePersistPendingTaskMutation,
  useDeletePendingTaskMutation,
  useLazyGetTaskPendingIdQuery,
  useLazyGetDrugsShortageQuery,
  useGetActiveTaskQuery,
  useGetTasksArchiveQuery,
  useApproveEventProtocolMutation,
  useUpdateTaskIndividualsMutation,
  useUpdateTaskIndividualMutation,
  useHasWithUnexpensedDrugsQuery,
} = vaccinationTaskApi;
